import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Users, ChevronLeft, ChevronRight, Play, ArrowUp, ArrowDown, ArrowUpDown, Search, X } from "lucide-react";
import { SectionCard } from "@/components/common/Primitives";

// Planning-list status palette. Maps the backend `derived_status` key to a coloured
// pill. Keep in sync with server-side _planning_derived_status().
const PLANNING_STATUS = {
  planned:                    { label: "Planned",                 bg: "#E2E8F0", fg: "#334155", border: "#CBD5E1", dot: "#94A3B8" },
  in_progress:                { label: "In Progress",             bg: "#FFEDD5", fg: "#9A3412", border: "#FDBA74", dot: "#F97316" },
  pending_review:             { label: "Pending Review",          bg: "#FEF3C7", fg: "#78350F", border: "#FCD34D", dot: "#F59E0B" },
  pre_audit_approved:         { label: "Pre-Audit Approved",      bg: "#D1FAE5", fg: "#065F46", border: "#6EE7B7", dot: "#10B981" },
  post_audit_in_progress:     { label: "Post-Audit In Progress",  bg: "#E0E7FF", fg: "#3730A3", border: "#A5B4FC", dot: "#6366F1" },
  post_audit_pending_review:  { label: "Post-Audit Pending Rev.", bg: "#FEF3C7", fg: "#78350F", border: "#FCD34D", dot: "#F59E0B" },
  closed:                     { label: "Closed",                  bg: "#1E3A8A", fg: "#FFFFFF", border: "#1E40AF", dot: "#1E3A8A" },
};

function PlanningStatusBadge({ status, label, testId }) {
  const s = PLANNING_STATUS[status] || PLANNING_STATUS.planned;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-[0.08em] border"
      style={{ background: s.bg, color: s.fg, borderColor: s.border }}
    >
      {label || s.label}
    </span>
  );
}

import { StageBadge } from "@/components/common/StageBadge";
import { TID } from "@/constants/testIds";
import { useAuth, hasRole } from "@/context/AuthContext";

function monthKey(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }

export default function PlanningPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = hasRole(user, "sd_admin");
  const [plans, setPlans] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [assignDlg, setAssignDlg] = useState(null);
  const [selectedAuditors, setSelectedAuditors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [refDate, setRefDate] = useState(new Date());
  const [view, setView] = useState("calendar");
  // List-view controls
  const [listStatus, setListStatus] = useState("all");
  const [listSupplier, setListSupplier] = useState("all");
  const [listAuditor, setListAuditor] = useState("all");
  const [listSearch, setListSearch] = useState("");
  const [sortKey, setSortKey] = useState("proximity"); // proximity | planned_date | audit_number | supplier | plant | status
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const load = async () => {
    const [p, s, pl, u] = await Promise.all([
      api.get("/planning"), api.get("/suppliers"), api.get("/plants"), api.get("/users"),
    ]);
    setPlans(p.data); setSuppliers(s.data); setPlants(pl.data);
    setAuditors(u.data.filter((x) => x.role === "auditor"));
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, []);

  const openNew = (defaults = {}) => {
    setEditing(null);
    setForm({ supplier_id: "", pre_audit_date: "", post_audit_date: "", audit_month: monthKey(refDate), audit_type: "Regular", remarks: "", ...defaults });
    setDlg(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      supplier_id: p.supplier_id,
      pre_audit_date: p.pre_audit_date || p.planned_date || "",
      post_audit_date: p.post_audit_date || "",
      audit_month: p.audit_month,
      audit_type: p.audit_type || "Regular", remarks: p.remarks || "",
    });
    setDlg(true);
  };
  const save = async () => {
    // Client-side validation
    if (!form.supplier_id) return toast.error("Please select a supplier");
    if (!form.pre_audit_date) return toast.error("Pre-Audit Date is required");
    if (!form.post_audit_date) return toast.error("Post-Audit Date is required");
    if (form.post_audit_date < form.pre_audit_date) {
      return toast.error("Post-Audit Date cannot be earlier than Pre-Audit Date");
    }
    try {
      // Auto-derive plant_id from the selected supplier's master data
      const sup = suppliers.find((s) => s.id === form.supplier_id);
      const payload = { ...form, plant_id: sup?.plant_id || null };
      if (editing) await api.put(`/planning/${editing.id}`, payload);
      else await api.post("/planning", payload);
      toast.success(editing ? "Plan updated" : "Plan created");
      setDlg(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm("Delete this plan and its audit?")) return;
    try { await api.delete(`/planning/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openAssign = (p) => {
    setAssignDlg(p);
    setSelectedAuditors(p.assigned_auditors || []);
  };
  const saveAssign = async () => {
    try {
      await api.post(`/planning/${assignDlg.id}/assign`, { plan_id: assignDlg.id, auditor_ids: selectedAuditors });
      toast.success("Auditors assigned");
      setAssignDlg(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const supById = useMemo(() => Object.fromEntries(suppliers.map((s) => [s.id, s])), [suppliers]);
  const plantById = useMemo(() => Object.fromEntries(plants.map((p) => [p.id, p])), [plants]);
  const auditorById = useMemo(() => Object.fromEntries(auditors.map((a) => [a.id, a])), [auditors]);

  // ---- List view: filter + sort + paginate ----
  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);
  const filteredSorted = useMemo(() => {
    let out = plans.slice();
    if (listStatus !== "all") out = out.filter((p) => p.status === listStatus);
    if (listSupplier !== "all") out = out.filter((p) => p.supplier_id === listSupplier);
    if (listAuditor !== "all") {
      if (listAuditor === "__unassigned") out = out.filter((p) => (p.assigned_auditors || []).length === 0);
      else out = out.filter((p) => (p.assigned_auditors || []).includes(listAuditor));
    }
    if (listSearch.trim()) {
      const q = listSearch.trim().toLowerCase();
      out = out.filter((p) => {
        const sup = supById[p.supplier_id]?.name || "";
        const plant = plantById[p.plant_id]?.name || "";
        return (
          (p.audit_number || "").toLowerCase().includes(q) ||
          sup.toLowerCase().includes(q) ||
          plant.toLowerCase().includes(q) ||
          (p.planned_date || "").includes(q)
        );
      });
    }
    // Sort
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => {
      if (sortKey === "proximity") {
        const da = Math.abs(new Date(a.planned_date).getTime() - today.getTime());
        const db = Math.abs(new Date(b.planned_date).getTime() - today.getTime());
        return (da - db) * dir;
      }
      if (sortKey === "planned_date") {
        return (new Date(a.planned_date).getTime() - new Date(b.planned_date).getTime()) * dir;
      }
      if (sortKey === "supplier") {
        return (supById[a.supplier_id]?.name || "").localeCompare(supById[b.supplier_id]?.name || "") * dir;
      }
      if (sortKey === "plant") {
        return (plantById[a.plant_id]?.name || "").localeCompare(plantById[b.plant_id]?.name || "") * dir;
      }
      if (sortKey === "status") return (a.status || "").localeCompare(b.status || "") * dir;
      if (sortKey === "audit_number") return (a.audit_number || "").localeCompare(b.audit_number || "") * dir;
      return 0;
    });
    return out;
  }, [plans, listStatus, listSupplier, listAuditor, listSearch, sortKey, sortDir, supById, plantById, today]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = filteredSorted.slice(pageStart, pageStart + pageSize);

  // Reset to page 1 when filters/sort change
  useEffect(() => { setPage(1); }, [listStatus, listSupplier, listAuditor, listSearch, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }
  function sortIcon(key) {
    if (sortKey !== key) return <ArrowUpDown size={11} className="inline ml-1 text-slate-400" />;
    return sortDir === "asc"
      ? <ArrowUp size={11} className="inline ml-1 text-sams-primary" />
      : <ArrowDown size={11} className="inline ml-1 text-sams-primary" />;
  }
  function daysBadge(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
    const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
    if (diff === 0) return <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-sm">TODAY</span>;
    if (diff > 0 && diff <= 7) return <span className="ml-1 text-[10px] font-semibold text-sams-primary bg-blue-100 px-1.5 py-0.5 rounded-sm">D-{diff}</span>;
    if (diff > 7 && diff <= 30) return <span className="ml-1 text-[10px] font-semibold text-slate-500">D-{diff}</span>;
    if (diff < 0 && diff >= -7) return <span className="ml-1 text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-sm">D+{-diff}</span>;
    return null;
  }
  function clearFilters() {
    setListStatus("all"); setListSupplier("all"); setListAuditor("all"); setListSearch("");
  }
  const activeFilterCount = [listStatus, listSupplier, listAuditor].filter((v) => v !== "all").length + (listSearch ? 1 : 0);

  const y = refDate.getFullYear();
  const m = refDate.getMonth();
  const monthPlans = plans.filter((p) => p.audit_month === monthKey(refDate));
  const daysCount = daysInMonth(y, m);
  const firstDay = new Date(y, m, 1).getDay(); // 0=Sun
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysCount; d++) cells.push(d);

  const monthName = refDate.toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Monthly Planning</div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Audit Planning</h1>
          <p className="text-sm text-slate-600 mt-1">Schedule supplier audits and assign auditors.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            <button onClick={() => setView("calendar")} className={`px-3 py-1.5 text-sm ${view === "calendar" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="view-calendar">Calendar</button>
            <button onClick={() => setView("list")} className={`px-3 py-1.5 text-sm ${view === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`} data-testid="view-list">List</button>
          </div>
          {canWrite && (
            <Button data-testid={TID.planning.createBtn} onClick={() => openNew()} className="bg-sams-primary hover:bg-sams-primaryHover text-white h-9">
              <Plus size={14} className="mr-1" /> New Plan
            </Button>
          )}
        </div>
      </div>

      {view === "calendar" ? (
        <SectionCard
          title={monthName}
          subtitle={`${monthPlans.length} plan(s) this month`}
          actions={
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" onClick={() => setRefDate(new Date(y, m - 1, 1))} className="h-8 w-8"><ChevronLeft size={16} /></Button>
              <Button size="sm" variant="outline" onClick={() => setRefDate(new Date())} className="h-8">Today</Button>
              <Button size="icon" variant="ghost" onClick={() => setRefDate(new Date(y, m + 1, 1))} className="h-8 w-8"><ChevronRight size={16} /></Button>
            </div>
          }
        >
          <div className="grid grid-cols-7 gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="px-2 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} className="min-h-[92px] bg-slate-50/50 rounded-md" />;
              const dateStr = `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
              // Build entries: each plan may show on its pre_audit_date AND/OR post_audit_date
              const dayEntries = [];
              monthPlans.forEach((p) => {
                const preDate = p.pre_audit_date || p.planned_date;
                if (preDate === dateStr) dayEntries.push({ plan: p, stage: "pre_audit", audit_id: p.pre_audit_id || p.audit_id });
              });
              plans.forEach((p) => {
                if (p.post_audit_date === dateStr) dayEntries.push({ plan: p, stage: "post_audit", audit_id: p.post_audit_id });
              });
              const isToday = dateStr === new Date().toISOString().slice(0, 10);
              return (
                <div key={i} className={`min-h-[92px] p-2 border rounded-md ${isToday ? "border-sams-primary bg-blue-50/40" : "border-slate-200 bg-white"} hover:bg-slate-50 transition-colors group cursor-pointer`}
                  onClick={() => canWrite && dayEntries.length === 0 && openNew({ pre_audit_date: dateStr, audit_month: monthKey(refDate) })}>
                  <div className="text-xs font-bold text-slate-700">{d}</div>
                  <div className="mt-1 space-y-1">
                    {dayEntries.slice(0, 2).map((e, idx) => {
                      const derived = e.plan.derived_status || e.plan.status || "planned";
                      const palette = PLANNING_STATUS[derived] || PLANNING_STATUS.planned;
                      return (
                        <div key={idx} onClick={(ev) => { ev.stopPropagation(); if (e.audit_id) nav(`/audits/${e.audit_id}`); }}
                          data-testid={`calendar-event-${e.plan.id}-${idx}`}
                          className="text-[10px] px-1.5 py-1 rounded-sm truncate cursor-pointer border"
                          style={{ background: palette.bg, color: palette.fg, borderColor: palette.border }}
                          title={`${supById[e.plan.supplier_id]?.name || ""} · ${e.stage === "post_audit" ? "Post-Audit" : "Pre-Audit"} · ${palette.label}`}>
                          <span className="mr-1 font-bold" style={{ opacity: 0.85 }}>
                            {e.stage === "post_audit" ? "POST" : "PRE"}
                          </span>
                          {supById[e.plan.supplier_id]?.name || "—"}
                        </div>
                      );
                    })}
                    {dayEntries.length > 2 && <div className="text-[10px] text-slate-500">+{dayEntries.length - 2} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      ) : (
        <SectionCard
          title="All Plans"
          subtitle={`${filteredSorted.length} plan(s) · Page ${currentPage} of ${totalPages}`}
          actions={
            activeFilterCount > 0 && (
              <button onClick={clearFilters} data-testid="planning-clear-filters"
                className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors">
                <X size={12} /> Clear {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
              </button>
            )
          }
        >
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-slate-200">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={listSearch} onChange={(e) => setListSearch(e.target.value)}
                data-testid="planning-search"
                placeholder="Search audit #, supplier, plant, date..."
                className="h-9 pl-8 w-72" />
            </div>
            <Select value={listStatus} onValueChange={setListStatus}>
              <SelectTrigger data-testid="planning-filter-status" className="w-48 h-9"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="pre_audit_approved">Pre-Audit Approved</SelectItem>
                <SelectItem value="post_audit_in_progress">Post-Audit In Progress</SelectItem>
                <SelectItem value="post_audit_pending_review">Post-Audit Pending Review</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={listSupplier} onValueChange={setListSupplier}>
              <SelectTrigger data-testid="planning-filter-supplier" className="w-56 h-9"><SelectValue placeholder="Supplier" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All suppliers</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={listAuditor} onValueChange={setListAuditor}>
              <SelectTrigger data-testid="planning-filter-auditor" className="w-48 h-9"><SelectValue placeholder="Auditor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All auditors</SelectItem>
                <SelectItem value="__unassigned">Unassigned</SelectItem>
                {auditors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
                  <th className="text-left px-3 py-2 cursor-pointer hover:text-slate-900 select-none" onClick={() => toggleSort("audit_number")} data-testid="sort-audit-number">Audit # {sortIcon("audit_number")}</th>
                  <th className="text-left px-3 py-2 cursor-pointer hover:text-slate-900 select-none" onClick={() => toggleSort("supplier")} data-testid="sort-supplier">Supplier {sortIcon("supplier")}</th>
                  <th className="text-left px-3 py-2 cursor-pointer hover:text-slate-900 select-none" onClick={() => toggleSort("plant")} data-testid="sort-plant">Plant {sortIcon("plant")}</th>
                  <th className="text-left px-3 py-2 cursor-pointer hover:text-slate-900 select-none" onClick={() => toggleSort("proximity")} data-testid="sort-proximity" title="Closest to today first">
                    Planned Date {sortIcon("proximity")}
                    {sortKey === "proximity" && <span className="ml-1 normal-case tracking-normal text-[9px] text-slate-400">(H-day)</span>}
                  </th>
                  <th className="text-left px-3 py-2">Auditors</th>
                  <th className="text-left px-3 py-2 cursor-pointer hover:text-slate-900 select-none" onClick={() => toggleSort("status")} data-testid="sort-status">Status {sortIcon("status")}</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500 text-sm italic">No plans match your filters.</td></tr>
                )}
                {pageRows.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-2 font-mono text-xs">{p.audit_number}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">{supById[p.supplier_id]?.name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {(() => {
                        const plantId = p.plant_id || supById[p.supplier_id]?.plant_id;
                        const plant = plantById[plantId];
                        return plant ? (
                          <div>
                            <div className="font-medium text-slate-800">{plant.name}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{plant.code}</div>
                          </div>
                        ) : <span className="text-slate-400">—</span>;
                      })()}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sky-700">PRE:</span>&nbsp;{p.pre_audit_date || p.planned_date}{daysBadge(p.pre_audit_date || p.planned_date)}
                      </div>
                      {p.post_audit_date && (
                        <div className="flex items-center mt-1 opacity-75">
                          <span className="text-indigo-700">POST:</span>&nbsp;{p.post_audit_date}{daysBadge(p.post_audit_date)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {(p.assigned_auditors || []).length === 0 ? <span className="text-red-600 font-semibold">Unassigned</span> :
                        (p.assigned_auditors || []).map((aid) => auditorById[aid]?.name?.split(" ")[0] || "?").join(", ")}
                    </td>
                    <td className="px-3 py-2"><PlanningStatusBadge status={p.derived_status || p.status} label={p.derived_status_label} testId={`planning-status-${p.id}`} /></td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => nav(`/audits/${p.audit_id}`)} className="h-8 px-2"><Play size={13} /></Button>
                      {canWrite && (
                        <>
                          <Button size="sm" variant="ghost" data-testid={TID.planning.assignBtn(p.id)} onClick={() => openAssign(p)} className="h-8 px-2 text-sams-primary"><Users size={13} /></Button>
                          <Button size="sm" variant="ghost" data-testid={TID.planning.editBtn(p.id)} onClick={() => openEdit(p)} className="h-8 px-2"><Pencil size={13} /></Button>
                          <Button size="sm" variant="ghost" data-testid={TID.planning.deleteBtn(p.id)} onClick={() => remove(p)} className="h-8 px-2 text-red-600 hover:bg-red-50"><Trash2 size={13} /></Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredSorted.length > 0 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
              <div className="text-xs text-slate-500">
                Showing <span className="font-semibold text-slate-900">{pageStart + 1}</span>–
                <span className="font-semibold text-slate-900">{Math.min(pageStart + pageSize, filteredSorted.length)}</span>
                {" of "}<span className="font-semibold text-slate-900">{filteredSorted.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => setPage(1)} disabled={currentPage === 1}
                  data-testid="pagination-first" className="h-8 px-2 text-xs">« First</Button>
                <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                  data-testid="pagination-prev" className="h-8 px-2">
                  <ChevronLeft size={13} />
                </Button>
                {(() => {
                  const pages = [];
                  const start = Math.max(1, currentPage - 2);
                  const end = Math.min(totalPages, start + 4);
                  for (let i = start; i <= end; i++) pages.push(i);
                  return pages.map((n) => (
                    <button key={n} onClick={() => setPage(n)} data-testid={`pagination-page-${n}`}
                      className={`h-8 min-w-[32px] px-2 text-xs rounded-md border transition-colors ${
                        n === currentPage
                          ? "bg-sams-primary border-sams-primary text-white font-semibold"
                          : "border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}>{n}</button>
                  ));
                })()}
                <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  data-testid="pagination-next" className="h-8 px-2">
                  <ChevronRight size={13} />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}
                  data-testid="pagination-last" className="h-8 px-2 text-xs">Last »</Button>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Create"} Audit Plan</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Supplier *</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="plan-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.supplier_id && (() => {
                const sup = suppliers.find((s) => s.id === form.supplier_id);
                const plant = plants.find((p) => p.id === sup?.plant_id);
                return (
                  <div className="mt-2 flex items-center gap-3 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Plant (from master data)</div>
                      <div className="font-semibold text-slate-900 mt-0.5" data-testid="plan-plant-derived">
                        {plant ? `${plant.code} · ${plant.name}` : <span className="text-amber-600">No plant assigned to this supplier</span>}
                      </div>
                    </div>
                    <div className="ml-auto">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Category</div>
                      <div className="font-semibold text-slate-900 mt-0.5">{sup?.category || "—"}</div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Audit Month *</Label>
                <Input type="month" value={form.audit_month} onChange={(e) => setForm({ ...form, audit_month: e.target.value })} className="mt-1 h-10" data-testid="plan-audit-month" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Pre-Audit Date *</Label>
                <Input type="date" value={form.pre_audit_date} onChange={(e) => setForm({ ...form, pre_audit_date: e.target.value })} className="mt-1 h-10" data-testid="plan-pre-audit-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Post-Audit Date *</Label>
              <Input
                type="date"
                min={form.pre_audit_date || undefined}
                value={form.post_audit_date}
                onChange={(e) => setForm({ ...form, post_audit_date: e.target.value })}
                className="mt-1 h-10" data-testid="plan-post-audit-date"
              />
              <div className="text-[10px] text-slate-500 mt-1">Follow-up audit after CAPA closure. Must be on or after Pre-Audit Date.</div>
              {form.pre_audit_date && form.post_audit_date && form.post_audit_date < form.pre_audit_date && (
                <div className="text-[11px] text-red-600 mt-1 font-semibold" data-testid="plan-date-error">
                  ⚠ Post-Audit Date cannot be earlier than Pre-Audit Date.
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Audit Type</Label>
              <Select value={form.audit_type} onValueChange={(v) => setForm({ ...form, audit_type: v })}>
                <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Regular">Regular</SelectItem>
                  <SelectItem value="Special">Special</SelectItem>
                  <SelectItem value="Follow-up">Follow-up</SelectItem>
                  <SelectItem value="New Supplier">New Supplier</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancel</Button>
            <Button data-testid={TID.planning.saveBtn} onClick={save} className="bg-sams-primary hover:bg-sams-primaryHover text-white">
              {editing ? "Save changes" : "Create plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign auditors dialog */}
      <Dialog open={!!assignDlg} onOpenChange={(v) => !v && setAssignDlg(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Assign Auditors</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {auditors.map((a) => (
              <label key={a.id} className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={selectedAuditors.includes(a.id)}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedAuditors([...selectedAuditors, a.id]);
                    else setSelectedAuditors(selectedAuditors.filter((x) => x !== a.id));
                  }} />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{a.name}</div>
                  <div className="text-xs text-slate-500">{a.email}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDlg(null)}>Cancel</Button>
            <Button onClick={saveAssign} className="bg-sams-primary hover:bg-sams-primaryHover text-white" data-testid="assign-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
